import React, { useCallback, useEffect, useRef, useState } from 'react';

import SecurityIcon from '@mui/icons-material/Security';
import { Box, styled, Typography } from '@mui/material';
import colors from 'theme/colors';

interface OTPInputProps {
  length?: number;
  onComplete?: (otp: string) => void;
  onChange?: (otp: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  hasError?: boolean;
}

const StyledInput = styled('input')<{ hasError?: boolean; isFilled?: boolean }>(
  ({ theme, hasError, isFilled }) => ({
    width: '56px',
    height: '56px',
    border: hasError ? '2px solid #ff4757' : '2px solid #e0e6ed',
    borderRadius: '16px',
    textAlign: 'center',
    fontSize: '24px',
    fontWeight: '600',
    color: '#2f3542',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    outline: 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxSizing: 'border-box',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
    '&:hover': {
      borderColor: colors.gradientColor2,
      transform: 'translateY(-3px) scale(1.05)',
      boxShadow: `0 12px 30px ${colors.gradientColor2}40`,
    },
    '&:focus': {
      borderColor: colors.gradientColor2,
      boxShadow: `0 0 0 4px ${colors.gradientColor2}30`,
      transform: 'translateY(-3px) scale(1.05)',
      background: 'linear-gradient(135deg, #ffffff 0%, #e3f2fd 100%)',
    },
    '&:disabled': {
      backgroundColor: '#f1f2f6',
      color: '#a4b0be',
      cursor: 'not-allowed',
      borderColor: '#ddd',
      transform: 'none',
      boxShadow: 'none',
    },
    '&::placeholder': {
      color: '#a4b0be',
    },
  })
);

const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  onComplete,
  onChange,
  disabled = false,
  autoFocus = true,
  hasError = false,
}) => {
  const [otp, setOtp] = useState<string[]>(new Array(length).fill(''));
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [shouldFocusNext, setShouldFocusNext] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      const inputElement = inputRefs.current[0];
      if (inputElement) {
        inputElement.focus();
      }
    }
  }, [autoFocus]);

  // Handle focus after state updates
  useEffect(() => {
    if (shouldFocusNext !== null && inputRefs.current[shouldFocusNext]) {
      const inputElement = inputRefs.current[shouldFocusNext];
      if (inputElement) {
        inputElement.focus();
      }
      setShouldFocusNext(null);
    }
  }, [shouldFocusNext]);

  const handleChange = useCallback(
    (index: number, value: string) => {
      if (disabled) return;

      // Only allow single digit
      if (value.length > 1) {
        value = value.slice(-1);
      }

      // Only allow numbers
      if (!/^\d*$/.test(value)) {
        return;
      }

      setOtp((prevOtp) => {
        const newOtp = [...prevOtp];
        newOtp[index] = value;
        const otpString = newOtp.join('');

        // Call onChange with the new value
        onChange?.(otpString);

        // Check if OTP is complete
        if (otpString.length === length && !otpString.includes('')) {
          onComplete?.(otpString);
        }

        return newOtp;
      });

      // Auto-move to next input when current is filled
      if (value && index < length - 1) {
        // Move to next input
        const nextIndex = index + 1;
        setActiveIndex(nextIndex);
        setShouldFocusNext(nextIndex);
      } else if (value && index === length - 1) {
        // If it's the last input and filled, keep focus on it
        setActiveIndex(index);
      }
    },
    [disabled, length, onChange, onComplete]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent) => {
      if (disabled) return;

      if (e.key === 'Backspace') {
        e.preventDefault();

        setOtp((prevOtp) => {
          if (prevOtp[index]) {
            // Clear current input
            const newOtp = [...prevOtp];
            newOtp[index] = '';
            return newOtp;
          } else if (index > 0) {
            // Move to previous input and clear it
            const prevIndex = index - 1;
            setActiveIndex(prevIndex);
            setShouldFocusNext(prevIndex);
            const newOtp = [...prevOtp];
            newOtp[prevIndex] = '';
            return newOtp;
          }
          return prevOtp;
        });

        // Handle onChange after state update
        if (otp[index]) {
          const newOtp = [...otp];
          newOtp[index] = '';
          onChange?.(newOtp.join(''));
        } else if (index > 0) {
          const newOtp = [...otp];
          newOtp[index - 1] = '';
          onChange?.(newOtp.join(''));
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        const prevIndex = index - 1;
        setActiveIndex(prevIndex);
        setShouldFocusNext(prevIndex);
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        const nextIndex = index + 1;
        setActiveIndex(nextIndex);
        setShouldFocusNext(nextIndex);
      } else if (e.key === 'Delete') {
        e.preventDefault();
        setOtp((prevOtp) => {
          const newOtp = [...prevOtp];
          newOtp[index] = '';
          return newOtp;
        });

        // Handle onChange after state update
        const newOtp = [...otp];
        newOtp[index] = '';
        onChange?.(newOtp.join(''));
      }
    },
    [disabled, length, onChange]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      if (disabled) return;

      e.preventDefault();
      const pastedData = e.clipboardData.getData('text');
      const pastedDigits = pastedData.replace(/\D/g, '').slice(0, length);

      if (pastedDigits.length > 0) {
        const newOtp = new Array(length).fill('');
        for (let i = 0; i < pastedDigits.length; i++) {
          newOtp[i] = pastedDigits[i];
        }
        const otpString = newOtp.join('');

        setOtp(newOtp);
        onChange?.(otpString);

        // Focus on the next empty input or the last input
        const nextIndex = Math.min(pastedDigits.length, length - 1);
        setActiveIndex(nextIndex);
        setShouldFocusNext(nextIndex);

        // Check if OTP is complete
        if (pastedDigits.length === length) {
          onComplete?.(otpString);
        }
      }
    },
    [disabled, length, onChange, onComplete]
  );

  const handleFocus = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={3}
      sx={{
        maxWidth: '500px',
        margin: '0 auto',
        padding: '20px',
      }}
    >
      {/* Header with Icon */}
      <Box display="flex" alignItems="center" gap={2}>
        <SecurityIcon
          sx={{
            fontSize: 32,
            color: colors.gradientColor2,
            filter: `drop-shadow(0 4px 8px ${colors.gradientColor2}40)`,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            background: `linear-gradient(45deg, ${colors.gradientColor1} 0%, ${colors.gradientColor2} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: '600',
            display: 'inline-block',
          }}
        >
          Enter Verification Code
        </Typography>
      </Box>

      {/* OTP Input Boxes */}
      <Box
        display="flex"
        gap={2}
        justifyContent="center"
        alignItems="center"
        flexWrap="wrap"
      >
        {otp.map((digit, index) => (
          <StyledInput
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={() => handleFocus(index)}
            disabled={disabled}
            maxLength={1}
            autoComplete="off"
            hasError={hasError}
            isFilled={digit !== ''}
          />
        ))}
      </Box>
    </Box>
  );
};

export default OTPInput;
